import { ref } from 'vue';
import {
  isPlannedRangeComplete,
  plannedRangeFromAnchorStops,
  plannedRangesEqual,
  type PlannedRangeValue,
} from '@/components/shipment/form/utils/shipment-form-planned-range-sync';
import type { ShipmentFormStopRow } from '@/stores/shipment-form/stop-row';

export function createShipmentFormMetadataSlice() {
  const generalName = ref('');
  const generalCarrier = ref('');
  const generalNotes = ref('');
  const generalAssetValueCents = ref<number | null>(null);
  const generalPlannedRange = ref<[Date | null, Date | null] | null>(null);
  const persistedMetadataSnapshot = ref<string | null>(null);

  function normalizeOptionalMetadataText(
    value: string | null | undefined,
  ): string | null {
    const trimmed = value?.trim() ?? '';
    return trimmed === '' ? null : trimmed;
  }

  function metadataPayloadSnapshot(
    name: string,
    carrier: string | null | undefined,
    notes: string | null | undefined,
    assetValueCents: number | null | undefined,
  ): string {
    return JSON.stringify({
      name: name.trim(),
      carrier: normalizeOptionalMetadataText(carrier),
      notes: normalizeOptionalMetadataText(notes),
      asset_value: assetValueCents ?? null,
    });
  }

  function markMetadataPersisted(
    name: string,
    carrier: string | null | undefined,
    notes: string | null | undefined,
    assetValueCents: number | null | undefined,
  ) {
    persistedMetadataSnapshot.value = metadataPayloadSnapshot(
      name,
      carrier,
      notes,
      assetValueCents,
    );
  }

  function haveMetadataChangedSincePersisted(
    name: string,
    carrier: string | null | undefined,
    notes: string | null | undefined,
    assetValueCents: number | null | undefined,
  ): boolean {
    if (persistedMetadataSnapshot.value == null) return true;
    return (
      metadataPayloadSnapshot(name, carrier, notes, assetValueCents) !==
      persistedMetadataSnapshot.value
    );
  }

  function resetMetadata() {
    generalName.value = '';
    generalCarrier.value = '';
    generalNotes.value = '';
    generalAssetValueCents.value = null;
    generalPlannedRange.value = null;
    persistedMetadataSnapshot.value = null;
  }

  function syncGeneralPlannedRangeFromStops(stops: ShipmentFormStopRow[]) {
    const next = plannedRangeFromAnchorStops(stops);
    if (!plannedRangesEqual(generalPlannedRange.value, next)) {
      generalPlannedRange.value = next;
    }
  }

  function applyGeneralPlannedRangeToAnchorStops(
    stops: ShipmentFormStopRow[],
    patchStop: (key: string, patch: Partial<ShipmentFormStopRow>) => void,
    markAnchorPlannedSlotsManual: () => void,
    unmarkAnchorPlannedSlotsManual: () => void,
  ) {
    if (stops.length < 2) return;
    const range: PlannedRangeValue = generalPlannedRange.value;
    if (range == null) {
      patchStop(stops[0]!.key, { plannedDeparture: null });
      patchStop(stops[stops.length - 1]!.key, { plannedArrival: null });
      unmarkAnchorPlannedSlotsManual();
      return;
    }
    const [start, end] = range;
    patchStop(stops[0]!.key, { plannedDeparture: start });
    patchStop(stops[stops.length - 1]!.key, { plannedArrival: end });
    if (isPlannedRangeComplete(range)) {
      markAnchorPlannedSlotsManual();
    } else {
      unmarkAnchorPlannedSlotsManual();
    }
  }

  return {
    generalName,
    generalCarrier,
    generalNotes,
    generalAssetValueCents,
    generalPlannedRange,
    persistedMetadataSnapshot,
    markMetadataPersisted,
    haveMetadataChangedSincePersisted,
    resetMetadata,
    syncGeneralPlannedRangeFromStops,
    applyGeneralPlannedRangeToAnchorStops,
  };
}
